import service from '@/store/services/economicModel-service';

const state = {
  economicModel: null,
  namespaced: true,
};

const mutations = {
  SET_RESOURCE: (state, economicModel) => {
    state.economicModel = economicModel;
  }
};

const actions = {
  get({commit, dispatch}, params) {
    return service.get(params)
      .then((economicModel) => {
        commit('SET_RESOURCE', economicModel.list);
      });
  },
  update({commit, dispatch}, params) {
    commit('SET_RESOURCE', params.economic_model);
  },
};

const getters = {
  economicModel: state => state.economicModel,
  avgSalePrice: state => state.economicModel.avg_sale_price,
  averageCommission: (state, getters, rootState, rootGetters) => {
    return Number(state.economicModel.avg_sale_price) * (Number(state.economicModel.avg_commission_percent) / 100);
  },
  closedUnits: (state, getters, rootState, rootGetters) => {
    return Math.round(getters.companyGci / getters.averageCommission);
  },
  closedSalesVolume: (state, getters, rootState, rootGetters) => {
    return getters.closedUnits * state.economicModel.avg_sale_price;
  },
  companyGci: (state, getters, rootState, rootGetters) => {
    const yearlyExpenses = rootGetters['cos/yearlyExpenses'];
    return Number(state.economicModel.net_profit_goal) + yearlyExpenses + Number(state.economicModel.cos);
  },
  grossProfit: (state, getters, rootState, rootGetters) => {
    return getters.companyGci - Number(getters.calculatedCos);
  },
  profitPercent: (state, getters, rootState, rootGetters) => {
    return (100 - getters.expensePercent - getters.cosPercent).toFixed(2);
  },
  calculatedCos: (state, getters, rootState, rootGetters) => {
    const annualFranchiseFee = rootGetters['cos/annualFranchiseFee'];
    const additionalCos = rootGetters['cos/additionalCos'];
    let cos = (Number(getters.paidToBroker) + 
        Number(annualFranchiseFee)) + 
        Number(getters.teamListingsGci) + 
        Number(getters.teamBuyersGci) + 
        Number(getters.calculatedIsa) + 
        Number(getters.referralFees) + 
        Number(getters.adminFees) + 
        Number(additionalCos);
    return cos ? cos : 0;
  },
  totalApptsHeld: (state, getters, rootState, rootGetters) => {
    return (getters.totalListingsTaken / (Number(state.economicModel.listings_taken_percent) / 100)).toFixed();
  },
  totalListingsTaken: (state, getters, rootState, rootGetters) => {
    return (getters.totalListingsSold / (Number(state.economicModel.listings_taken_to_close) / 100)).toFixed();
  },
  totalListingsSold: (state, getters, rootState, rootGetters) => {
    return (getters.closedUnits * (Number(state.economicModel.percent_of_business) / 100)).toFixed();
  },
  totalBuyersHeld: (state, getters, rootState, rootGetters) => {
    return (getters.totalBuyersTaken / (Number(state.economicModel.buyers_taken_percent) / 100)).toFixed();
  },
  totalBuyersTaken: (state, getters, rootState, rootGetters) => {
    return (getters.totalBuyersSold / (Number(state.economicModel.buyers_taken_to_close) / 100)).toFixed();
  }, 
  totalBuyersSold: (state, getters, rootState, rootGetters) => {
    return (getters.closedUnits * ((100 - Number(state.economicModel.percent_of_business)) / 100)).toFixed();
  },
  expensePercent: (state, getters, rootState, rootGetters) => {
    const yearlyExpenses = rootGetters['cos/yearlyExpenses'];
    return (yearlyExpenses / getters.companyGci * 100).toFixed(2);
  },
  cosPercent: (state, getters, rootState, rootGetters) => {
    return (getters.calculatedCos / getters.companyGci * 100).toFixed(2);
  },
  totalSeasonality: (state, getters, rootState, rootGetters) => {
    return Number(state.economicModel.seasonality_q1) + Number(state.economicModel.seasonality_q2) + Number(state.economicModel.seasonality_q3) + Number(state.economicModel.seasonality_q4);
  },
  listingsSold: (state, getters, rootState, rootGetters) => {
    const listingsSold = rootGetters['cos/listingsSold'];
    return getters.totalListingsSold - Number(listingsSold);
  },
  buyersSold: (state, getters, rootState, rootGetters) => {
    const buyersSold = rootGetters['cos/buyersSold'];
    return getters.totalBuyersSold - Number(buyersSold);
  },
  paidToBroker: (state, getters, rootState, rootGetters) => {
    const cos = rootGetters['cos/cos'];
    if(cos.broker_yearly_max) {
      return Number(cos.broker_dollar_max);
    } else {
      return getters.companyGci * (Number(cos.broker_percent) / 100);
    }
  },
  teamListings: (state, getters, rootState, rootGetters) => {
    const cos = rootGetters['cos/cos'];
    if(cos.team_member == 0) {
      return Number(getters.listingsSold) * Number(getters.averageCommission) * (Number(cos.listings_commission_percent) / 100);
    } else if(cos.team_member == 1) {
      return Number(getters.totalListingsSold) * Number(getters.averageCommission) * (Number(100 - cos.listings_commission_percent) / 100);
    } else {
      return 0;
    }
  },
  teamBuyers: (state, getters, rootState, rootGetters) => {
    const cos = rootGetters['cos/cos'];
    if(cos.team_member == 0) {
      return Number(getters.buyersSold) * Number(getters.averageCommission) * (Number(cos.buyers_commission_percent) / 100);
    } else if(cos.team_member == 1) {
      return Number(getters.totalBuyersSold) * Number(getters.averageCommission) * (Number(100 - cos.buyers_commission_percent) / 100);
    } else {
      return 0;
    }
  },
  isaTotal: (state, getters, rootState, rootGetters) => {
    const cos = rootGetters['cos/cos'];
    return getters.closedUnits * (Number(cos.isa_percent_total) / 100);
  },
  isaComp: (state, getters, rootState, rootGetters) => {
    const cos = rootGetters['cos/cos'];
    return getters.averageCommission * (Number(cos.isa_percent) / 100);
  },
  calculatedIsa: (state, getters, rootState, rootGetters) => {
    return Math.round(getters.isaTotal * getters.isaComp);
  },
  teamListingsGci: (state, getters, rootState, rootGetters) => {
    return getters.teamListings;
  },
  teamBuyersGci: (state, getters, rootState, rootGetters) => {
    return getters.teamBuyers;
  },
  referralFees: (state, getters, rootState, rootGetters) => {
    const cos = rootGetters['cos/cos'];
    return Number(getters.averageCommission) * (Number(cos.referral_fee_percent) / 100) * Number(cos.referral_fee_count);
  },
  listingAdminFees: (state, getters, rootState, rootGetters) => {
    const cos = rootGetters['cos/cos'];
    if(cos.transaction_fee) {
      return Number(getters.totalListingsSold) * Number(cos.transaction_fee_amount);
    } else {
      return 0;
    }
  },
  buyerAdminFees: (state, getters, rootState, rootGetters) => {
    const cos = rootGetters['cos/cos'];
    if(cos.buyer_transaction_fee == 1) {
      return Number(getters.totalBuyersSold) * Number(cos.buyer_transaction_fee_amount);
    } else {
      return 0;
    }
  },
  adminFees: (state, getters, rootState, rootGetters) => {
    return getters.listingAdminFees + getters.buyerAdminFees;
  },
};

const economicModel = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};

export default economicModel;
